export const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:3000";

// Eriol Go is browser/PWA only — no Electron path here (unlike guest).
const CLIENT_APP = "mobile";

const RETRYABLE_HTTP_STATUS = new Set([502, 503, 504]);

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`HTTP ${status}: ${body}`);
    this.name = "HttpError";
  }
}

function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)eriol_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function isRetryableMethod(method: string): boolean {
  return method.toUpperCase() === "GET";
}

function isRetryableNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("fetch failed") ||
    msg.includes("networkerror")
  );
}

function getRetryDelayMs(attempt: number): number {
  return Math.min(300 * 2 ** (attempt - 1), 1500);
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

function buildHeaders(method: string): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Client-App": CLIENT_APP,
  };

  // Auth rides the HttpOnly `eriol_session` cookie (credentials: "include").
  // Mutations need the double-submit CSRF token. Even unauthenticated
  // mutations (login) need it if a session cookie happens to be present —
  // the middleware enforces it whenever a session cookie rides the request.
  if (!SAFE_METHODS.has(method.toUpperCase())) {
    const csrf = readCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  return headers;
}

async function request<T>(
  method: string,
  endpoint: string,
  data?: unknown,
  auth = false,
): Promise<T> {
  const normalizedMethod = method.toUpperCase();

  // `credentials: "include"` ships the HttpOnly `eriol_session` cookie.
  const opts: RequestInit = {
    method: normalizedMethod,
    headers: buildHeaders(normalizedMethod),
    credentials: "include",
  };

  if (data) opts.body = JSON.stringify(data);

  const maxAttempts = isRetryableMethod(normalizedMethod) ? 4 : 1;
  const requestUrl = `${API_BASE_URL}${endpoint}`;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let response: Response;

    try {
      response = await fetch(requestUrl, opts);
    } catch (error) {
      const shouldRetry =
        attempt < maxAttempts && isRetryableNetworkError(error);
      if (!shouldRetry) throw error;
      await sleep(getRetryDelayMs(attempt));
      continue;
    }

    if (!response.ok) {
      if (response.status === 401 && auth) {
        window.dispatchEvent(new CustomEvent("auth:session-expired"));
      }

      const text = await response.text();
      const shouldRetry =
        attempt < maxAttempts && RETRYABLE_HTTP_STATUS.has(response.status);

      if (shouldRetry) {
        await sleep(getRetryDelayMs(attempt));
        continue;
      }

      throw new HttpError(response.status, text);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  throw new Error(
    `Request failed after ${maxAttempts} attempts: ${normalizedMethod} ${endpoint}`,
  );
}

/**
 * WHIP needs a raw `application/sdp` POST (not JSON) and the SDP answer comes
 * back as text + a Location header — so it bypasses the JSON request() above
 * but reuses the cookie/CSRF/X-Client-App conventions.
 */
export async function authPostSdp(
  endpoint: string,
  sdp: string,
): Promise<{ answer: string; location: string | null }> {
  const csrf = readCsrfToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/sdp",
      "X-Client-App": CLIENT_APP,
      ...(csrf ? { "X-CSRF-Token": csrf } : {}),
    },
    credentials: "include",
    body: sdp,
  });

  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:session-expired"));
    }
    throw new HttpError(response.status, await response.text());
  }

  return {
    answer: await response.text(),
    location: response.headers.get("Location"),
  };
}

export const api = {
  get: <T>(endpoint: string) => request<T>("GET", endpoint),

  post: <T>(endpoint: string, data?: unknown) =>
    request<T>("POST", endpoint, data),

  put: <T>(endpoint: string, data: unknown) =>
    request<T>("PUT", endpoint, data),

  patch: <T>(endpoint: string, data?: unknown) =>
    request<T>("PATCH", endpoint, data),

  authGet: <T>(endpoint: string) =>
    request<T>("GET", endpoint, undefined, true),

  authPost: <T>(endpoint: string, data?: unknown) =>
    request<T>("POST", endpoint, data, true),

  authPut: <T>(endpoint: string, data: unknown) =>
    request<T>("PUT", endpoint, data, true),

  authPatch: <T>(endpoint: string, data?: unknown) =>
    request<T>("PATCH", endpoint, data, true),

  authDelete: <T>(endpoint: string) =>
    request<T>("DELETE", endpoint, undefined, true),
};
